import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { I3EventCarouselSlide } from '../cms/types';
import { resolvePublicMediaSrc } from '../utils/i3Assets';

const AUTO_MS = 6500;

type Props = {
  slides: I3EventCarouselSlide[];
  onOpen: (type: 'video' | 'image', src: string, title?: string) => void;
  className?: string;
};

const I3EventsHeroCarousel: React.FC<Props> = ({ slides, onOpen, className }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const fn = () => setReducedMotion(mq.matches);
    mq.addEventListener?.('change', fn);
    return () => mq.removeEventListener?.('change', fn);
  }, []);

  const len = slides.length;

  const go = useCallback(
    (dir: -1 | 1) => {
      if (len === 0) return;
      setIndex((i) => (i + dir + len) % len);
    },
    [len],
  );

  useEffect(() => {
    if (len <= 1 || paused || reducedMotion) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    timerRef.current = setInterval(() => go(1), AUTO_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [len, paused, reducedMotion, go]);

  useEffect(() => {
    if (index >= len && len > 0) setIndex(0);
  }, [index, len]);

  if (len === 0) {
    return (
      <div className={`i3-events-hero-carousel i3-events-hero-carousel--empty ${className ?? ''}`}>
        <p>
          Add images or videos under <strong>eventsPage → latestHighlight → carouselSlides</strong> in your site
          content JSON.
        </p>
      </div>
    );
  }

  const slide = slides[index];
  const slideSrc = resolvePublicMediaSrc(slide.src);
  const posterSrc = slide.poster ? resolvePublicMediaSrc(slide.poster) : undefined;

  return (
    <div
      className={`i3-events-hero-carousel ${className ?? ''}`}
      role="region"
      aria-roledescription="carousel"
      aria-label="Event highlights"
    >
      <div className="i3-events-hero-carousel-viewport">
        {slide.type === 'image' ? (
          <button
            type="button"
            className="i3-events-hero-carousel-slide i3-events-hero-carousel-slide--image"
            onClick={() => onOpen('image', slideSrc, slide.title)}
            aria-label={slide.title || 'View image fullscreen'}
          >
            <div className="i3-events-hero-carousel-media">
              <img src={slideSrc} alt={slide.title || ''} className="i3-events-hero-carousel-img" />
            </div>
          </button>
        ) : (
          <button
            type="button"
            className="i3-events-hero-carousel-slide i3-events-hero-carousel-slide--video"
            onClick={() => onOpen('video', slideSrc, slide.title)}
            aria-label={slide.title || 'Play video fullscreen'}
          >
            <div className="i3-events-hero-carousel-media">
              {posterSrc ? (
                <img src={posterSrc} alt="" className="i3-events-hero-carousel-img" />
              ) : (
                <video
                  className="i3-events-hero-carousel-img i3-events-hero-carousel-video-preview"
                  src={slideSrc}
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden
                />
              )}
              <span className="i3-events-hero-carousel-play" aria-hidden>
                ▶
              </span>
            </div>
          </button>
        )}
        {slide.caption ? (
          <p className="i3-events-hero-carousel-caption" aria-live="polite">
            {slide.caption}
          </p>
        ) : null}
      </div>

      {len > 1 ? (
        <>
          <div className="i3-events-hero-carousel-controls">
            <button type="button" className="i3-events-hero-carousel-btn" onClick={() => go(-1)} aria-label="Previous slide">
              ‹
            </button>
            <button type="button" className="i3-events-hero-carousel-btn" onClick={() => go(1)} aria-label="Next slide">
              ›
            </button>
          </div>
          <div className="i3-events-hero-carousel-dots" role="tablist" aria-label="Slide indicators">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === index}
                className={`i3-events-hero-carousel-dot${i === index ? ' i3-events-hero-carousel-dot--active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Slide ${i + 1} of ${len}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="i3-events-hero-carousel-pause"
            onClick={() => setPaused((p) => !p)}
            aria-pressed={paused}
          >
            {paused || reducedMotion ? 'Play slideshow' : 'Pause slideshow'}
          </button>
        </>
      ) : null}
    </div>
  );
};

export default I3EventsHeroCarousel;
